import { useState, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  BookOpen,
  Heart,
  LogIn,
  MessageCircle,
  Clock3,
  Zap,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Coins,
  Sun,
  Compass,
  Flame,
  Sunrise,
  Sunset,
  Moon,
  Facebook,
  Instagram,
  Users,
  Youtube,
  Gift,
  Star,
} from "lucide-react";
import logoIcon96 from "@/assets/logo-icon-96.webp";
import {
  getBoideToken,
  getHomepageReviews,
  getMonthlyTopupProgress,
  type DailyFortuneCard,
  type DailyFortuneResponse,
  type HomepageReview,
  type StreakMe,
} from "@/lib/boide-api";
import { loadDailyFortuneMe, loadMyStreak } from "@/lib/bootstrap";
import { useBoideUser, displayName } from "@/hooks/use-boide-user";
import { useRegisterTour } from "@/components/tour/tour-provider";
import { HomeSuperApp } from "@/components/home-super-app";
import { BusinessInfoBlock } from "@/components/business-info-block";
import lotus from "@/assets/lotus-3d.webp";
import lotus192 from "@/assets/lotus-3d-192.webp";
import lotus320 from "@/assets/lotus-3d-320.webp";

// GIF trong suốt 1×1 — fallback cho <picture> khi không source nào khớp viewport,
// để ảnh của breakpoint khác không bị tải thừa.
const BLANK_IMG = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

function getGreeting(): { text: string; icon: typeof Sun } {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return { text: "Chào buổi sáng", icon: Sunrise };
  if (h >= 11 && h < 14) return { text: "Chào buổi trưa", icon: Sun };
  if (h >= 14 && h < 18) return { text: "Chào buổi chiều", icon: Sunset };
  if (h >= 18 && h < 23) return { text: "Chào buổi tối", icon: Moon };
  return { text: "Chào đêm khuya", icon: Moon };
}

function ResponsiveHomePage() {
  return <HomeSuperApp />;
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Bói Đê - Gieo Quẻ Kinh Dịch, Lập Lá Số Tử Vi Miễn Phí 2026" },
      {
        name: "description",
        content: "Gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự, xem vận khí hôm nay cùng Bói Đê.",
      },
      {
        property: "og:title",
        content: "Bói Đê - Gieo Quẻ Kinh Dịch, Lập Lá Số Tử Vi Miễn Phí 2026",
      },
      {
        property: "og:description",
        content: "Gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự, xem vận khí hôm nay cùng Bói Đê.",
      },
      { property: "og:url", content: "https://boide.online/" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/" }],
  }),
  component: ResponsiveHomePage,
});

function HomePromoCarousel() {
  const [current, setCurrent] = useState(0);
  const promoSlides = 6;
  // Swipe state — ref để không re-render trong lúc kéo
  const swipeStart = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const justSwiped = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Phụ thuộc `current` và `promoSlides` để mỗi lần user tự lướt/bấm dot, đồng hồ auto-play đếm lại từ đầu
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % promoSlides);
    }, 7000);
    return () => clearInterval(timer);
  }, [current, promoSlides]);

  // Đăng ký sự kiện touch thủ công với passive: false để chặn cuộn dọc khi lướt ngang trên mobile
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let isSwiping = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      isSwiping = false;
      swipeStart.current = { x: touch.clientX, y: touch.clientY, pointerId: -1 };
      justSwiped.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!swipeStart.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      // Xác định xem có phải vuốt ngang chủ đạo hay không
      if (!isSwiping && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        if (Math.abs(dx) > Math.abs(dy)) {
          isSwiping = true;
        }
      }

      if (isSwiping) {
        if (e.cancelable) {
          e.preventDefault(); // Chặn cuộn dọc trang
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      if (!start) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;

      // Chỉ nhận cử chỉ ngang rõ ràng, tránh ăn nhầm tap và cuộn dọc
      if (Math.abs(dx) > 32 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        justSwiped.current = true;
        setCurrent(
          (previous) =>
            (((previous + (dx < 0 ? 1 : -1)) % promoSlides) + promoSlides) % promoSlides,
        );
      }
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [promoSlides]);

  const goTo = (idx: number) => setCurrent(((idx % promoSlides) + promoSlides) % promoSlides);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    if (e.pointerType === "touch") return; // Touch events are handled separately via useEffect
    // Nhấn xuống trên link/nút (CTA, dot) → nhường nguyên sự kiện cho phần tử đó.
    // KHÔNG setPointerCapture ở đây: khi capture đang giữ, trình duyệt bắn `click`
    // vào section thay vì link bên dưới → mọi nút trong carousel chết với chuột.
    if ((e.target as HTMLElement).closest("a,button")) return;
    swipeStart.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    justSwiped.current = false;
    // Giữ quyền nhận pointerup kể cả khi ngón tay trượt ra ngoài Link/card.
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return; // Touch events are handled separately via useEffect
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || start.pointerId !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    // Chỉ nhận cử chỉ ngang rõ ràng, tránh ăn nhầm tap và cuộn dọc
    if (Math.abs(dx) > 32 && Math.abs(dx) > Math.abs(dy) * 1.15) {
      justSwiped.current = true;
      setCurrent(
        (previous) => (((previous + (dx < 0 ? 1 : -1)) % promoSlides) + promoSlides) % promoSlides,
      );
    }
  };

  // Sau khi vuốt, chặn click "rơi" vào Link/nút bên trong card
  const handleClickCapture = (e: React.MouseEvent) => {
    if (justSwiped.current) {
      e.preventDefault();
      e.stopPropagation();
      justSwiped.current = false;
    }
  };

  return (
    <section
      ref={containerRef}
      className="relative grid w-full touch-pan-y select-none grid-cols-1 grid-rows-1 cursor-grab active:cursor-grabbing"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={(e) => {
        if (e.pointerType !== "touch") {
          swipeStart.current = null;
        }
      }}
      onClickCapture={handleClickCapture}
      // Link mặc định draggable — chặn native drag để không nuốt mất pointerup khi kéo chuột
      onDragStart={(e) => e.preventDefault()}
    >
      {/* 1. Bói Live Card */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 0
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-gold-soft bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {/* Soundwave background watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="sound-glow" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <circle cx="150" cy="50" r="42" stroke="url(#sound-glow)" strokeWidth="1.5" />
              <circle
                cx="150"
                cy="50"
                r="28"
                stroke="url(#sound-glow)"
                strokeWidth="2.5"
                strokeDasharray="5 3"
              />
              <circle cx="150" cy="50" r="14" fill="url(#sound-glow)" opacity="0.2" />
              <path
                d="M 50,50 C 75,20 100,80 125,50 C 150,20 175,80 200,50"
                stroke="url(#sound-glow)"
                strokeWidth="2"
              />
              <path
                d="M 30,50 C 60,35 90,65 120,50 C 150,35 180,65 210,50"
                stroke="url(#sound-glow)"
                strokeWidth="1"
                opacity="0.6"
              />
              <path
                d="M 70,50 C 90,10 110,90 130,50 C 150,10 170,90 190,50"
                stroke="url(#sound-glow)"
                strokeWidth="0.8"
                opacity="0.4"
              />
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-primary">
                <Sparkles className="h-2.5 w-2.5 text-primary" /> Mới
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">Đàm đạo qua </span>
                <span className="hidden md:inline">Đàm đạo trực tiếp với thầy AI qua </span>
                <span className="text-gold-gradient font-semibold">Bói Live</span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              Trò chuyện{" "}
              <strong className="font-bold text-primary">trực tiếp bằng giọng nói</strong> để luận
              giải lá số và gieo quẻ.
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Trò chuyện{" "}
              <strong className="font-bold text-primary">trực tiếp bằng giọng nói</strong>: Thầy
              nghe tâm sự, tự động gieo quẻ Kinh Dịch, lập lá số và luận giải ngay lập tức.
            </p>
            {/* Mobile copy details */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] sm:text-xs text-foreground/80 leading-[1.4] truncate md:hidden">
              <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-primary">
                BOILIVEOPENBETA
              </span>
              <span className="text-muted-foreground text-[9px] sm:text-[11px] font-normal italic">
                (Nhập mã giftcode nhận 20.000đ)
              </span>
            </div>
            {/* Desktop copy details */}
            <div className="hidden md:flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground/80 leading-[1.4]">
              <span>Đặc quyền VIP:</span>
              <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-primary">
                BOILIVEOPENBETA
              </span>
              <span className="text-muted-foreground text-[10px] sm:text-[11px] font-normal italic">
                (Nhập mã giftcode nhận 20.000đ lộc thỉnh thầy)
              </span>
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <Link
              to="/boi-live"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gold-gradient px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <MessageCircle className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Gọi ngay
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </section>
      </div>

      {/* 2. Se Duyên Card */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 1
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 -translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/10 via-pink-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-rose-500/5 blur-3xl pointer-events-none" />

          {/* Constellation background watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="rose-glow" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ec4899" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="#f43f5e" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Overlapping matching orbits */}
              <circle cx="120" cy="50" r="32" stroke="url(#rose-glow)" strokeWidth="2" />
              <circle
                cx="155"
                cy="50"
                r="32"
                stroke="url(#rose-glow)"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
              {/* Diamond match grid */}
              <path
                d="M 120,50 L 137.5,30 L 155,50 L 137.5,70 Z"
                fill="url(#rose-glow)"
                opacity="0.12"
                stroke="url(#rose-glow)"
                strokeWidth="1"
              />
              {/* Constellation lines and dots */}
              <line x1="120" y1="50" x2="155" y2="50" stroke="url(#rose-glow)" strokeWidth="1.5" />
              <circle cx="120" cy="50" r="3.5" fill="#f43f5e" />
              <circle cx="155" cy="50" r="3.5" fill="#ec4899" />
              <circle cx="137.5" cy="30" r="2.5" fill="#ec4899" />
              <circle cx="137.5" cy="70" r="2.5" fill="#f43f5e" />
              {/* Soft outer glow orbit */}
              <circle
                cx="137.5"
                cy="50"
                r="48"
                stroke="url(#rose-glow)"
                strokeWidth="0.8"
                opacity="0.5"
              />
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-rose-500/25 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                <Heart className="h-2.5 w-2.5 text-rose-500" /> Kết Nối
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">Ghép đôi </span>
                <span className="hidden md:inline">Tìm kiếm tình duyên cùng </span>
                <span className="bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent font-semibold">
                  Se Duyên
                </span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              So khớp bản mệnh thông minh dựa trên phân tích{" "}
              <strong className="font-bold text-rose-500 dark:text-rose-400">
                tương quan lá số
              </strong>
              .
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              So khớp bản mệnh thông minh: Tìm kiếm một nửa tâm đầu ý hợp dựa trên phân tích{" "}
              <strong className="font-bold text-rose-500 dark:text-rose-400">
                tương quan lá số
              </strong>{" "}
              Tử Vi, Tứ Trụ và sở thích cá nhân.
            </p>
            {/* Desktop extra info */}
            <div className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Trải nghiệm tính năng ghép đôi độc bản, giúp bạn tìm kiếm người có tần số năng lượng
              phù hợp nhất.
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <Link
              to="/se-duyen"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gradient-to-r from-rose-400 via-pink-500 to-rose-600 px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-white shadow-[0_4px_12px_rgba(244,63,94,0.3)] hover:shadow-[0_6px_20px_rgba(244,63,94,0.45)] border border-pink-300/25 transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <Heart className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Thử ngay
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </section>
      </div>

      {/* 3. Features & Promo Card */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 2
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 -translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-gold-soft/50 bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {/* Star visual watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="gold-glow-3" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g transform="translate(145, 50)">
                {/* Outer astrological compass ring */}
                <circle r="38" stroke="url(#gold-glow-3)" strokeWidth="1" />
                {/* Middle division ring */}
                <circle r="29" stroke="url(#gold-glow-3)" strokeWidth="2.5" strokeDasharray="6 3" />
                {/* Inner Hexagon representing Metaphysics / Kinh Dich */}
                <polygon
                  points="0,-20 17.32,-10 17.32,10 0,20 -17.32,10 -17.32,-10"
                  stroke="url(#gold-glow-3)"
                  strokeWidth="1.8"
                />
                {/* Core energetic core */}
                <circle r="8" fill="url(#gold-glow-3)" opacity="0.25" />
                {/* Axis lines extending outward */}
                <line
                  x1="0"
                  y1="-44"
                  x2="0"
                  y2="44"
                  stroke="url(#gold-glow-3)"
                  strokeWidth="0.8"
                  opacity="0.6"
                />
                {/* Diagonal constellation stars */}
                <circle cx="21" cy="-21" r="2" fill="var(--primary)" opacity="0.8" />
                <circle cx="-21" cy="21" r="2" fill="var(--primary)" opacity="0.8" />
                <circle cx="-21" cy="-21" r="2" fill="var(--primary)" opacity="0.8" />
                <circle cx="21" cy="21" r="2" fill="var(--primary)" opacity="0.8" />
              </g>
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-primary">
                <Sparkles className="h-2.5 w-2.5 text-primary" /> Giới thiệu
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">
                  Luận mệnh cùng <span className="text-gold-gradient font-semibold">AI</span>
                </span>
                <span className="hidden md:inline">
                  Luận giải vận mệnh chuyên sâu bằng{" "}
                  <span className="text-gold-gradient font-semibold">Trí Tuệ Nhân Tạo</span>
                </span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              Gieo quẻ Kinh Dịch, lập lá số Tử Vi, Bát Tự và phân tích ngũ hành.
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Gieo quẻ Kinh Dịch, lập lá số Tử Vi, Bát Tự và phân tích ngũ hành bản mệnh chuẩn xác.
            </p>
            {/* Mobile details */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] sm:text-xs text-foreground/80 leading-[1.4] truncate md:hidden">
              <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-primary">
                TỈ GIÁ VÀNG
              </span>
              <span className="text-muted-foreground text-[9px] sm:text-[11px] font-normal italic">
                (1 VND = 1 TOKEN)
              </span>
            </div>
            {/* Desktop details */}
            <div className="hidden md:flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground/80 leading-[1.4]">
              <span>Đặc quyền nạp:</span>
              <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-primary">
                TỈ GIÁ VÀNG
              </span>
              <span className="text-muted-foreground text-[10px] sm:text-[11px] font-normal italic">
                (1 VND = 1 TOKEN lộc nạp)
              </span>
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <Link
              to="/thanh-toan"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gold-gradient px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <Zap className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Nạp ngay
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </section>
      </div>

      {/* 4. Community Card — CTA vào nhóm + các nền tảng mạng xã hội */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 3
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 -translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-gold-soft bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {/* Community constellation watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="community-glow" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g transform="translate(145, 50)">
                <circle r="38" stroke="url(#community-glow)" strokeWidth="1" />
                <circle
                  r="26"
                  stroke="url(#community-glow)"
                  strokeWidth="2"
                  strokeDasharray="5 3"
                />
                {/* Thành viên kết nối tam giác */}
                <line
                  x1="0"
                  y1="-26"
                  x2="24"
                  y2="13"
                  stroke="url(#community-glow)"
                  strokeWidth="1"
                />
                <line
                  x1="24"
                  y1="13"
                  x2="-24"
                  y2="13"
                  stroke="url(#community-glow)"
                  strokeWidth="1"
                />
                <line
                  x1="-24"
                  y1="13"
                  x2="0"
                  y2="-26"
                  stroke="url(#community-glow)"
                  strokeWidth="1"
                />
                <circle cx="0" cy="-26" r="3" fill="var(--primary)" opacity="0.8" />
                <circle cx="24" cy="13" r="3" fill="var(--primary)" opacity="0.8" />
                <circle cx="-24" cy="13" r="3" fill="var(--primary)" opacity="0.8" />
                <circle r="6" fill="url(#community-glow)" opacity="0.3" />
              </g>
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-primary">
                <Users className="h-2.5 w-2.5 text-primary" /> Cộng đồng
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">Hội quán </span>
                <span className="hidden md:inline">Tham gia hội quán </span>
                <span className="text-gold-gradient font-semibold">Bói Đê</span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              Vào nhóm nhận{" "}
              <strong className="font-bold text-primary">giftcode & lộc sự kiện</strong> sớm nhất,
              luận số cùng đồng đạo.
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Vào nhóm để nhận{" "}
              <strong className="font-bold text-primary">giftcode & lộc sự kiện</strong> sớm nhất,
              hỏi đáp và luận số cùng đồng đạo bốn phương.
            </p>
            {/* Social platform quick links */}
            <div className="flex items-center gap-1.5 md:gap-2 pt-0.5">
              <span className="hidden md:inline text-xs text-muted-foreground">Theo dõi tại:</span>
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full border border-gold-soft/30 bg-background/40 text-muted-foreground transition hover:border-primary/60 hover:text-primary active:scale-95"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <a
              href={FACEBOOK_GROUP_URL}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gold-gradient px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <Users className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Vào nhóm
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </section>
      </div>

      {/* 5. Social Review Reward Card — chia sẻ cảm nhận nhận 100k */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 4
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 -translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-gold-soft bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {/* Megaphone broadcast watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="review-glow" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g transform="translate(145, 50)">
                <circle r="38" stroke="url(#review-glow)" strokeWidth="1" />
                <circle r="27" stroke="url(#review-glow)" strokeWidth="2" strokeDasharray="5 3" />
                {/* Loa phát thanh */}
                <path
                  d="M -14,-6 L 0,-12 L 0,12 L -14,6 Z"
                  stroke="url(#review-glow)"
                  strokeWidth="1.8"
                  fill="url(#review-glow)"
                  opacity="0.35"
                />
                <rect
                  x="-20"
                  y="-5"
                  width="6"
                  height="10"
                  rx="1.5"
                  stroke="url(#review-glow)"
                  strokeWidth="1.5"
                />
                {/* Sóng lan toả */}
                <path d="M 6,-8 A 10 10 0 0 1 6,8" stroke="url(#review-glow)" strokeWidth="1.5" />
                <path
                  d="M 12,-13 A 17 17 0 0 1 12,13"
                  stroke="url(#review-glow)"
                  strokeWidth="1.2"
                  opacity="0.7"
                />
                <circle cx="24" cy="-20" r="2" fill="var(--primary)" opacity="0.8" />
                <circle cx="27" cy="0" r="2" fill="var(--primary)" opacity="0.8" />
                <circle cx="24" cy="20" r="2" fill="var(--primary)" opacity="0.8" />
              </g>
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-primary">
                <Gift className="h-2.5 w-2.5 text-primary" /> Sự kiện
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">Viết cảm nhận, nhận </span>
                <span className="hidden md:inline">Chia sẻ cảm nhận về Bói Đê, nhận </span>
                <span className="text-gold-gradient font-semibold">1.000.000đ</span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              Đăng bài về Bói Đê lên MXH, gửi link — bài được duyệt nhận{" "}
              <strong className="font-bold text-primary">1.000.000đ</strong> vào ví.
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Đăng bài cảm nhận về Bói Đê lên Facebook, TikTok... rồi gửi link cho chúng tôi. Bài
              được duyệt nhận ngay <strong className="font-bold text-primary">1.000.000đ</strong>{" "}
              vào ví bói.
            </p>
            <div className="text-[10px] sm:text-xs text-muted-foreground leading-[1.4] italic truncate">
              (Kết quả duyệt báo qua Hộp thư trong tối đa 8 ngày)
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <Link
              to="/cam-nhan"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gold-gradient px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <Gift className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Nhận 100k
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </section>
      </div>

      {/* 6. Affiliate Commission Card — giới thiệu nhận 10% hoa hồng trọn đời */}
      <div
        className={cn(
          "col-start-1 row-start-1 transition-all duration-500 ease-in-out h-full w-full",
          current === 5
            ? "opacity-100 translate-x-0 pointer-events-auto z-10"
            : "opacity-0 -translate-x-4 pointer-events-none -z-10",
        )}
      >
        <section className="h-full relative overflow-hidden rounded-2xl border border-gold-soft bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent px-2.5 py-3 pb-4.5 sm:px-3 sm:py-3 sm:pb-4.5 shadow-mystic backdrop-blur-md flex flex-row items-center justify-between gap-3">
          <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          {/* Coin & percent watermark */}
          <div className="absolute right-0 top-0 bottom-0 w-[45%] opacity-85 pointer-events-none hidden md:block">
            <svg viewBox="0 0 200 100" className="h-full w-full" fill="none">
              <defs>
                <linearGradient id="affiliate-glow" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.45" />
                  <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g transform="translate(145, 50)">
                <circle r="38" stroke="url(#affiliate-glow)" strokeWidth="1" />
                <circle
                  r="27"
                  stroke="url(#affiliate-glow)"
                  strokeWidth="2"
                  strokeDasharray="5 3"
                />
                {/* Đồng xu % */}
                <circle r="14" stroke="url(#affiliate-glow)" strokeWidth="1.8" />
                <line
                  x1="-7"
                  y1="7"
                  x2="7"
                  y2="-7"
                  stroke="url(#affiliate-glow)"
                  strokeWidth="1.5"
                />
                <circle
                  cx="-5.5"
                  cy="-5.5"
                  r="2.2"
                  stroke="url(#affiliate-glow)"
                  strokeWidth="1.2"
                />
                <circle cx="5.5" cy="5.5" r="2.2" stroke="url(#affiliate-glow)" strokeWidth="1.2" />
                {/* Mắt xích chia sẻ */}
                <line
                  x1="14"
                  y1="-14"
                  x2="26"
                  y2="-22"
                  stroke="url(#affiliate-glow)"
                  strokeWidth="1"
                />
                <line
                  x1="14"
                  y1="14"
                  x2="26"
                  y2="22"
                  stroke="url(#affiliate-glow)"
                  strokeWidth="1"
                />
                <circle cx="26" cy="-22" r="3" fill="var(--primary)" opacity="0.8" />
                <circle cx="26" cy="22" r="3" fill="var(--primary)" opacity="0.8" />
                <circle cx="-26" cy="0" r="2" fill="var(--primary)" opacity="0.6" />
              </g>
            </svg>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-w-[72%] md:max-w-4xl min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 font-sans text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-primary">
                <Coins className="h-2.5 w-2.5 text-primary" /> Hoa hồng
              </span>
              <h3 className="font-serif-soft text-[13px] sm:text-base md:text-lg text-foreground font-semibold truncate">
                <span className="md:hidden">Giới thiệu bạn, nhận </span>
                <span className="hidden md:inline">Giới thiệu bạn bè, nhận hoa hồng </span>
                <span className="text-gold-gradient font-semibold">10% trọn đời</span>
              </h3>
            </div>
            {/* Mobile copy */}
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] line-clamp-2 md:hidden">
              Chia sẻ link, nhận <strong className="font-bold text-primary">10%</strong> mỗi lần bạn
              bè nạp — <strong className="font-bold text-primary">rút được về ngân hàng</strong>.
            </p>
            {/* Desktop copy */}
            <p className="hidden md:block text-xs text-muted-foreground leading-[1.4]">
              Chia sẻ link của bạn, nhận <strong className="font-bold text-primary">10%</strong> mỗi
              lần bạn bè nạp tiền — hoa hồng{" "}
              <strong className="font-bold text-primary">rút được về tài khoản ngân hàng</strong>.
              Người được giới thiệu cũng được +10% lần nạp đầu.
            </p>
            <div className="text-[10px] sm:text-xs text-muted-foreground leading-[1.4] italic truncate">
              (Xem hướng dẫn chi tiết từ đặt mã đến rút tiền)
            </div>
          </div>
          <div className="shrink-0 flex items-center justify-end">
            <Link
              to="/huong-dan-hoa-hong"
              className="group inline-flex min-h-8 md:min-h-10 items-center justify-center gap-1 rounded-full bg-gold-gradient px-3 py-1.5 md:px-5 md:py-2.5 text-[11px] md:text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] active:scale-95 whitespace-nowrap"
            >
              <Coins className="h-3 w-3 md:h-3.5 md:w-3.5" />
              Xem hướng dẫn
              <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </section>
      </div>

      {/* Dots absolute positioned top right on mobile, bottom center on desktop */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5 z-20">
        {Array.from({ length: promoSlides }).map((_, dotIdx) => (
          <button
            key={dotIdx}
            onClick={() => setCurrent(dotIdx)}
            className="group relative flex h-5 px-1 md:h-7 md:px-1.5 items-center justify-center cursor-pointer"
            aria-label={`Slide ${dotIdx + 1}`}
          >
            <span
              className={cn(
                "h-1 rounded-full transition-all duration-300",
                dotIdx === current
                  ? current === 1
                    ? "w-3 bg-rose-500"
                    : "w-3 bg-primary"
                  : "w-1 bg-muted-foreground/30 group-hover:bg-muted-foreground/60",
              )}
            />
          </button>
        ))}
      </div>
    </section>
  );
}

function DashboardPage() {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useRegisterTour("home", isMounted);

  const today = isMounted
    ? new Date().toLocaleDateString("vi-VN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const { user } = useBoideUser();
  const name = isMounted ? displayName(user) : "Quý Khách";
  const greeting = isMounted ? getGreeting() : { text: "Chào bạn", icon: Sun };
  const hasToken = isMounted ? !!getBoideToken() : false;
  // Pre-mount (SSR + first client render) defaults to the guest layout so guests —
  // the majority of homepage visitors — get zero layout shift on the hero CTA cluster
  // (logged-in users absorb the one post-mount switch instead). Also better SSR/SEO content.
  const isGuest = isMounted ? !hasToken : true;
  const { data: dailyFortune } = useQuery({
    queryKey: ["daily-fortune"],
    queryFn: loadDailyFortuneMe,
    enabled: hasToken,
    staleTime: 1000 * 60 * 30,
  });
  const { data: streak } = useQuery({
    queryKey: ["streak-me"],
    queryFn: loadMyStreak,
    enabled: hasToken,
    staleTime: 1000 * 60 * 5,
  });
  const statCards = getDashboardStatCards(dailyFortune?.cards);

  return (
    <>
      <main className="flex-1 min-h-0 overflow-y-auto px-3 py-3 pb-24 md:px-8 md:pt-4.5 md:pb-8">
        <div className="mx-auto max-w-[1400px] space-y-3.5 md:space-y-4.5">
          {/* Promo banner for Bói Live & Se Duyên */}
          <HomePromoCarousel />

          {/* Greeting banner */}
          <section className="home-hero-wonder relative overflow-hidden rounded-3xl border border-gold-soft shadow-mystic backdrop-blur-md">
            <div className="absolute inset-0 stardust opacity-45" />
            <div className="absolute left-1/2 top-0 h-56 w-56 -translate-x-1/2 rounded-full bg-primary/15 blur-3xl md:h-80 md:w-80" />
            <div className="relative grid grid-cols-1 gap-2.5 px-4.5 py-3.5 sm:gap-4 sm:p-6 lg:grid-cols-[minmax(0,1.06fr)_minmax(380px,0.94fr)] lg:items-center lg:py-5 lg:px-7 xl:py-5.5 xl:px-10">
              <div className="sm:hidden">
                <MobileOracleMark />
              </div>
              <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-gold-soft bg-background/40 px-3 py-1 font-sans text-[11px] font-medium tracking-wide text-primary md:px-3.5 md:text-xs">
                  <Sparkles className="h-3 w-3" /> {today || "Đang kết nối..."}
                </span>
                <h1 className="sr-only">Bói Đê — Nền tảng Kinh Dịch và Tử Vi Đông phương</h1>
                <h2 className="max-w-3xl pb-1 font-serif-soft text-[2.1rem] leading-[1.2] sm:text-5xl lg:text-[2.75rem] xl:text-[3.25rem]">
                  {isGuest ? (
                    <>
                      Mở lá số, gieo quẻ,{" "}
                      <span className="text-gold-gradient italic inline-block pb-[0.18em] leading-[1.3]">
                        hỏi AI luận vận
                      </span>
                    </>
                  ) : (
                    <>
                      {greeting.text},{" "}
                      <span className="text-gold-gradient italic inline-block pb-[0.18em] leading-[1.3]">
                        {name}
                      </span>
                    </>
                  )}
                </h2>
                <p className="max-w-2xl text-[13.5px] leading-5.5 text-foreground/72 sm:text-[14.5px] sm:leading-relaxed md:text-[15.5px] md:leading-7">
                  {isGuest ? (
                    <>
                      <span className="sm:hidden">
                        Gieo quẻ, lập lá số và hỏi AI theo đúng dữ liệu của bạn.
                      </span>
                      <span className="hidden sm:inline">
                        Một nơi để hỏi những điều đang mắc kẹt: gieo quẻ Kinh Dịch, lập Tử Vi/Bát Tự
                        và trò chuyện tiếp với AI theo đúng dữ liệu của bạn.
                      </span>
                    </>
                  ) : (
                    "Hôm nay tinh tú giao hòa, vận khí khai thông. Một ngày tốt để khởi sự điều mới, gặp gỡ quý nhân và tịnh tâm chiêm nghiệm."
                  )}
                </p>

                <HomeTopupCTA />

                <div className="grid grid-cols-1 gap-2.5 pt-1 sm:flex sm:flex-wrap sm:items-center md:gap-3 md:pt-2 [&>a]:whitespace-nowrap">
                  {isGuest && (
                    <Link
                      to="/login"
                      className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gold-gradient px-5 py-3 text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.02] md:min-h-0 md:px-6 md:py-3"
                    >
                      <LogIn className="h-4 w-4" />
                      Đăng nhập để bắt đầu
                    </Link>
                  )}
                  <Link
                    to="/kinh-dich"
                    data-tour="home-cast-cta"
                    className={`group inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition hover:scale-[1.02] md:min-h-0 md:px-6 md:py-3 ${
                      isGuest
                        ? "border border-gold-soft bg-background/50 text-foreground hover:border-primary/60"
                        : "bg-gold-gradient text-primary-foreground shadow-gold"
                    }`}
                  >
                    {isGuest ? "Xem thử Kinh Dịch" : "Gieo quẻ hôm nay"}
                    <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                  <Link
                    to="/tu-vi"
                    data-tour="home-tuvi-cta"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-gold-soft bg-background/40 px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-primary/60 md:min-h-0 md:px-6 md:py-3"
                  >
                    Xem lá số Tử Vi
                  </Link>
                  <Link
                    to="/bat-tu"
                    data-tour="home-battu-cta"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-gold-soft bg-background/40 px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-primary/60 md:min-h-0 md:px-6 md:py-3"
                  >
                    Xem lá số Bát Tự
                  </Link>
                  <Link
                    to="/doan-gio-sinh"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-gold-soft bg-background/40 px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-primary/60 md:min-h-0 md:px-6 md:py-3"
                  >
                    Đoán giờ sinh
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <HeroOracleVisual isGuest={isGuest} />
              </div>
            </div>
          </section>

          <DailyOverview
            dailyFortune={dailyFortune}
            statCards={statCards}
            streak={streak}
            isGuest={isGuest}
            isMounted={isMounted}
          />

          {/* Feature cards */}
          <section data-tour="home-features" className="space-y-3 md:space-y-5">
            <div className="flex items-end justify-between px-1 lg:hidden">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                  Khám phá
                </p>
                <h2 className="mt-1 font-serif-soft text-2xl text-foreground">
                  Chọn cách xem phù hợp
                </h2>
              </div>
              <span className="text-xs text-muted-foreground sm:hidden">Vuốt để xem</span>
            </div>
            <div className="home-feature-rail scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:pb-0">
              <FeatureCard
                to="/kinh-dich-chi-tiet"
                kicker="Quẻ Dịch"
                title="Kinh Dịch"
                subtitle="Gieo ba đồng tiền cổ"
                description="Tham vấn 64 quẻ Dịch để soi chiếu nghi vấn và tìm hướng đi. Mỗi lần gieo là một cuộc đối thoại với thiên cơ."
                ordinal="01"
                tone="gold"
              />
              <FeatureCard
                to="/tu-vi-chi-tiet"
                kicker="Lá số"
                title="Tử Vi Đẩu Số"
                subtitle="Lập bản mệnh 12 cung"
                description="Xác lập lá số truyền thống với chính tinh và phụ tinh, luận giải tường tận về mệnh, thân, phụ mẫu, phu thê, tài bạch."
                ordinal="02"
                tone="jade"
              />
              <FeatureCard
                to="/bat-tu-chi-tiet"
                kicker="Tứ Trụ"
                title="Bát Tự"
                subtitle="Bốn trụ Can Chi"
                description="Lập lá số Bát Tự từ năm tháng ngày giờ sinh: Tứ Trụ, Nhật chủ, Thập thần, ngũ hành và đại vận — luận giải vận mệnh theo dụng thần."
                ordinal="03"
                tone="vermillion"
              />
            </div>
          </section>

          <HomeTestimonials />

          <SiteFooter />
        </div>
      </main>
    </>
  );
}

function getDayPhase(hour: number) {
  if (hour >= 5 && hour < 11) {
    return { title: "Khởi động ngày mới", note: "Ưu tiên việc cần sự tập trung và chủ động." };
  }
  if (hour >= 11 && hour < 14) {
    return { title: "Giữ nhịp ổn định", note: "Tạm nghỉ ngắn trước khi tiếp tục việc quan trọng." };
  }
  if (hour >= 14 && hour < 18) {
    return { title: "Hoàn tất việc chính", note: "Thu gọn đầu việc và tránh quyết định vội." };
  }
  if (hour >= 18 && hour < 23) {
    return { title: "Thu lại năng lượng", note: "Phù hợp để nhìn lại ngày và giữ tâm thư thái." };
  }
  return {
    title: "Thời gian nghỉ ngơi",
    note: "Gác lại việc chưa cấp thiết và phục hồi năng lượng.",
  };
}

function DailyOverview({
  dailyFortune,
  statCards,
  streak,
  isGuest,
  isMounted,
}: {
  dailyFortune?: DailyFortuneResponse;
  statCards: DashboardStatCard[];
  streak?: StreakMe;
  isGuest: boolean;
  isMounted: boolean;
}) {
  const energy = statCards.find((card) => card.key === "energy") ?? statCards[0];
  const direction = statCards.find((card) => card.key === "direction") ?? statCards[1];
  const element = statCards.find((card) => card.key === "element") ?? statCards[2];
  const energyScore = dailyFortune?.energy_score;
  // Pre-mount (SSR + client render đầu) phải tất định: getHours() phụ thuộc múi giờ,
  // nên new Date(0) ra 0h trên server (UTC) nhưng 7h ở client VN → hydration mismatch (React #418).
  const dayPhase = getDayPhase(isMounted ? new Date().getHours() : 7);
  const currentTime = isMounted
    ? new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  return (
    <section
      data-tour="home-daily"
      className="overflow-hidden rounded-3xl border border-gold-soft/70 bg-card/72 shadow-mystic backdrop-blur"
    >
      <div className="flex items-center justify-between border-b border-gold-soft/45 px-4 py-3.5 sm:px-6">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
            Hôm nay
          </p>
          <h2 className="mt-0.5 font-serif-soft text-2xl text-foreground">Nhịp vận của bạn</h2>
        </div>
        {dailyFortune?.lunar_date ? (
          <span className="rounded-full border border-gold-soft/60 bg-background/55 px-3 py-1 text-[11px] font-medium text-primary">
            {dailyFortune.lunar_date}
          </span>
        ) : null}
      </div>

      <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
        <div className="divide-y divide-gold-soft/35 lg:border-r lg:border-gold-soft/35">
          <div className="px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/12 text-amber-700 dark:text-amber-300">
                <Zap className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-foreground">Năng lượng</span>
                  <span className="text-sm font-semibold text-primary">
                    {isGuest
                      ? "Đăng nhập để xem"
                      : `${energy.value}${energyScore !== undefined ? ` · ${energyScore}%` : ""}`}
                  </span>
                </div>
                {energyScore !== undefined && !isGuest ? (
                  <div
                    className="mt-2 h-2 overflow-hidden rounded-full bg-primary/10"
                    role="progressbar"
                    aria-label={`Năng lượng hôm nay ${energyScore}%`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={energyScore}
                  >
                    <div
                      className="h-full rounded-full bg-gold-gradient transition-[width] duration-700"
                      style={{ width: `${energyScore}%` }}
                    />
                  </div>
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">
                  {isGuest ? "Đăng nhập để nhận nhịp vận cá nhân theo ngày sinh." : energy.hint}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-gold-soft/35">
            {[direction, element].map(({ icon: Icon, ...card }) => (
              <div key={card.key} className="min-w-0 px-4 py-4 sm:px-6">
                <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
                  <Icon className="h-3.5 w-3.5 text-primary" /> {card.label}
                </div>
                <p className="mt-1.5 truncate font-serif-soft text-xl text-foreground">
                  {card.value}
                </p>
                <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
                  {card.hint}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="divide-y divide-gold-soft/35 border-t border-gold-soft/35 lg:border-t-0">
          <Link
            to={isGuest ? "/login" : "/diem-danh"}
            className="group flex items-center gap-3 px-4 py-4 transition hover:bg-primary/5 sm:px-6 sm:py-5"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-300">
              <Flame className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-muted-foreground">Chuỗi bói của bạn</p>
              {isGuest ? (
                <p className="mt-0.5 text-sm font-semibold text-foreground">
                  Đăng nhập để bắt đầu chuỗi
                </p>
              ) : (
                <p className="mt-0.5 text-sm text-foreground">
                  <strong className="font-serif-soft text-2xl text-primary">
                    {streak?.current_streak ?? 0}
                  </strong>{" "}
                  ngày hiện tại · dài nhất {streak?.longest_streak ?? 0} ngày
                </p>
              )}
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
          </Link>

          <div className="flex items-center gap-3 px-4 py-4 sm:px-6 sm:py-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300">
              <Clock3 className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-medium text-muted-foreground">Nhịp hiện tại</p>
                <span className="text-xs font-semibold text-primary">{currentTime}</span>
              </div>
              <p className="mt-0.5 text-sm font-semibold text-foreground">{dayPhase.title}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{dayPhase.note}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileOracleMark() {
  return (
    <div
      className="relative mx-auto flex h-[92px] max-w-[180px] items-center justify-center"
      aria-hidden="true"
    >
      {/* Khối này chỉ hiện <640px (sm:hidden) nhưng img display:none vẫn bị tải —
          dùng <picture> media để viewport ≥sm không tốn request (fallback = data URI rỗng). */}
      <picture>
        <source
          media="(max-width: 639.98px)"
          srcSet={`${lotus192} 192w, ${lotus320} 320w`}
          sizes="96px"
        />
        <img
          src={BLANK_IMG}
          alt=""
          width={160}
          height={160}
          fetchPriority="high"
          className="relative w-[96px] animate-float drop-shadow-[0_12px_24px_rgba(184,115,18,0.2)]"
        />
      </picture>
    </div>
  );
}

type FeatureTone = "gold" | "jade" | "vermillion";

const FEATURE_TONE_CLASSES: Record<FeatureTone, string> = {
  gold: "text-amber-700 dark:text-amber-300",
  jade: "text-teal-700 dark:text-teal-300",
  vermillion: "text-rose-700 dark:text-rose-300",
};

function HeroOracleVisual({ isGuest, compact = false }: { isGuest: boolean; compact?: boolean }) {
  return (
    <div
      className={`home-oracle-visual relative mx-auto w-full max-w-[560px] ${compact ? "max-w-[330px]" : ""}`}
    >
      <div className="absolute inset-0 rounded-full bg-primary/15 blur-3xl" />
      <div className="relative mx-auto aspect-[1.05] min-h-[220px]">
        <div className="absolute left-1/2 top-[8%] z-10 flex -translate-x-1/2 flex-col items-center">
          <div className="relative">
            <div className="absolute inset-3 rounded-full bg-primary/25 blur-2xl" />
            {/* Chỉ hiện từ lg (hidden lg:block) — <picture> media chặn tải ảnh ở viewport nhỏ. */}
            <picture>
              <source
                media="(min-width: 1024px)"
                srcSet={`${lotus320} 320w, ${lotus} 460w`}
                sizes={compact ? "116px" : "(min-width: 1280px) 230px, 190px"}
              />
              <img
                src={BLANK_IMG}
                alt="Hoa sen vàng phát quang — biểu tượng Bói Đê"
                width={520}
                height={520}
                fetchPriority="high"
                className={`${compact ? "w-[116px]" : "w-[190px] xl:w-[230px]"} relative animate-float drop-shadow-[0_20px_45px_rgba(184,115,18,0.28)]`}
              />
            </picture>
          </div>
        </div>

        <div
          className={`absolute bottom-[4%] right-[4%] z-20 rounded-3xl border border-gold-soft/55 bg-card/90 p-4 shadow-[0_24px_70px_rgba(92,64,36,0.18)] backdrop-blur-xl dark:bg-card/80 ${
            compact ? "w-[210px] p-3" : "w-[285px]"
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-display text-[10px] uppercase tracking-[0.2em] text-primary">
              AI luận giải
            </span>
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <p
            className={`${compact ? "mt-2 text-[13px]" : "mt-3 text-sm"} font-medium leading-relaxed text-foreground`}
          >
            {isGuest
              ? "Đăng nhập để lưu lá số và hỏi tiếp theo từng vận hạn."
              : "Dữ liệu của bạn đã sẵn sàng cho phiên tham vấn tiếp theo."}
          </p>
          <div className="mt-3 space-y-1.5">
            {["Chọn phương pháp", "Nhận luận giải", "Hỏi sâu bằng AI"].map((step, index) => (
              <div key={step} className="flex items-center gap-2 text-[11px] text-foreground/68">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/12 text-[9px] font-bold text-primary">
                  {index + 1}
                </span>
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({
  to,
  kicker,
  title,
  subtitle,
  description,
  ordinal,
  tone,
}: {
  to: "/kinh-dich-chi-tiet" | "/tu-vi-chi-tiet" | "/bat-tu-chi-tiet";
  kicker: string;
  title: string;
  subtitle: string;
  description: string;
  ordinal: string;
  tone: FeatureTone;
}) {
  return (
    <Link
      to={to}
      className="group relative w-[82vw] max-w-[360px] shrink-0 snap-start overflow-hidden rounded-3xl border border-gold-soft bg-card/70 p-4 shadow-mystic backdrop-blur transition hover:border-primary/50 hover:shadow-gold sm:w-[48vw] sm:p-6 md:p-8 lg:w-auto lg:max-w-none"
    >
      <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl transition group-hover:bg-primary/20" />
      <div className="relative grid grid-cols-[80px_1fr] items-center gap-4 sm:grid-cols-[120px_1fr] md:grid-cols-[1.2fr_1fr] md:gap-6 md:flex-none">
        <div className="relative flex justify-center md:order-2">
          <div className="absolute inset-2 rounded-full bg-primary/8 blur-2xl" />
          <span
            aria-hidden="true"
            className={`relative font-serif-soft text-5xl font-semibold leading-none opacity-75 md:text-8xl ${FEATURE_TONE_CLASSES[tone]}`}
          >
            {ordinal}
          </span>
        </div>
        <div className="space-y-1.5 md:space-y-4 md:text-left md:order-1">
          <span className="inline-flex font-sans text-[10px] tracking-[0.12em] font-semibold text-primary uppercase md:text-[11px] mobile-label-uppercase">
            {kicker}
          </span>
          <div>
            <h3 className="font-serif-soft text-xl sm:text-2xl text-foreground md:text-3xl">
              {title}
            </h3>
            <p className="mt-0.5 text-xs italic text-muted-foreground md:text-sm">{subtitle}</p>
          </div>
          <p className="hidden text-sm leading-relaxed text-muted-foreground md:block">
            {description}
          </p>
          <div className="inline-flex items-center gap-1.5 pt-1 text-xs font-medium text-primary md:gap-2 md:pt-2 md:text-sm">
            Khám phá{" "}
            <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:translate-x-1 group-hover:-translate-y-1 md:h-4 md:w-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

type HomeTestimonial = {
  id: string;
  name: string;
  initials: string;
  experience: string;
  rating: number;
  content: string;
};

const HOME_TESTIMONIALS: HomeTestimonial[] = [
  {
    id: "nguyen-minh-anh",
    name: "Nguyễn Minh Anh",
    initials: "NA",
    experience: "Kinh Dịch",
    rating: 5,
    content: "Phần luận giải rất dễ hiểu, mình có thể hỏi tiếp đúng điều đang băn khoăn.",
  },
  {
    id: "tran-thu-ha",
    name: "Trần Thu Hà",
    initials: "TH",
    experience: "Tử Vi",
    rating: 5,
    content: "Lá số được trình bày rõ ràng, các phần công việc và tình cảm khá sát với mình.",
  },
  {
    id: "le-hoang-nam",
    name: "Lê Hoàng Nam",
    initials: "LN",
    experience: "Bói Live",
    rating: 5,
    content:
      "Mình thích cách Bói Live trò chuyện tự nhiên, không có cảm giác đang đọc một bài mẫu.",
  },
  {
    id: "pham-thanh-huyen",
    name: "Phạm Thanh Huyền",
    initials: "PH",
    experience: "Bát Tự",
    rating: 5,
    content:
      "Phần phân tích ngũ hành giúp mình hiểu rõ điểm mạnh và điều cần cân bằng trong thời gian tới.",
  },
  {
    id: "do-quang-minh",
    name: "Đỗ Quang Minh",
    initials: "ĐM",
    experience: "Kinh Dịch",
    rating: 5,
    content: "Mỗi phần đều có giải thích cụ thể nên người mới tìm hiểu như mình vẫn theo dõi được.",
  },
  {
    id: "vu-ngoc-linh",
    name: "Vũ Ngọc Linh",
    initials: "VL",
    experience: "Tử Vi",
    rating: 5,
    content:
      "Giao diện đẹp, thao tác nhanh và phần hỏi sâu giúp mình làm rõ được nhiều điều trong lá số.",
  },
];

const MAX_HOME_TESTIMONIALS = 6;
// Điểm sao hiển thị là con số cố định (marketing), không dùng avg thật từ BE.
const HOME_TESTIMONIAL_DISPLAY_AVG = "4.9";
const FALLBACK_TESTIMONIAL_COUNT = 328;

/** Chữ cái đầu của từ đầu + từ cuối trong tên (VD "Nguyễn Minh Anh" → "NA"). */
function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "BĐ";
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase() || "BĐ";
}

function toHomeTestimonial(review: HomepageReview): HomeTestimonial {
  return {
    id: review.id,
    name: review.display_name,
    initials: initialsFromName(review.display_name),
    experience: review.experience,
    rating: Math.min(5, Math.max(1, Math.round(review.rating))),
    content: review.content,
  };
}

function HomeTestimonials() {
  const testimonialRailRef = useRef<HTMLDivElement>(null);

  // Review thật do admin chọn; BE random tối đa 6 bài trong pool mỗi lần gọi.
  // staleTime Infinity: giữ nguyên bộ 6 bài trong suốt 1 lượt vào web.
  const { data: reviewsData } = useQuery({
    queryKey: ["homepage-reviews"],
    queryFn: () => getHomepageReviews(MAX_HOME_TESTIMONIALS),
    staleTime: Infinity,
  });

  // Load-more khi lướt ngang gần cuối: nạp thêm batch mới (BE loại id đã hiện).
  const [extraReviews, setExtraReviews] = useState<HomepageReview[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (reviewsData) setHasMore(Boolean(reviewsData.has_more));
  }, [reviewsData]);

  const seenIds = new Set<string>();
  const allReviews = [...(reviewsData?.items ?? []), ...extraReviews].filter((item) => {
    if (!item.content.trim() || seenIds.has(item.id)) return false;
    seenIds.add(item.id);
    return true;
  });
  const realTestimonials = allReviews.map(toHomeTestimonial);
  // Fallback về danh sách tĩnh khi API lỗi hoặc admin chưa chọn review nào.
  const testimonials = realTestimonials.length > 0 ? realTestimonials : HOME_TESTIMONIALS;

  const loadMoreReviews = async () => {
    if (loadingMoreRef.current || !hasMore || allReviews.length === 0) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const excludeIds = allReviews.map((r) => r.id);
      const res = await getHomepageReviews(MAX_HOME_TESTIMONIALS, excludeIds);
      const fresh = res.items.filter(
        (item) => item.content.trim().length > 0 && !excludeIds.includes(item.id),
      );
      setExtraReviews((prev) => [...prev, ...fresh]);
      setHasMore(Boolean(res.has_more) && res.items.length > 0);
    } catch {
      // Lỗi mạng → dừng load-more, giữ nguyên các review đang hiện.
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleRailScroll = () => {
    const rail = testimonialRailRef.current;
    if (!rail || !hasMore) return;
    const remainingRight = rail.scrollWidth - (rail.scrollLeft + rail.clientWidth);
    // Còn chưa đầy ~1.5 card nữa là chạm cuối → nạp trước cho liền mạch.
    if (remainingRight < 540) void loadMoreReviews();
  };

  // IntersectionObserver trên sentinel cuối rail: bắt cả scroll bằng nút mũi tên /
  // snap animation — những trường hợp onScroll có thể không kịp bắn.
  const railEndRef = useRef<HTMLDivElement>(null);
  const loadMoreFnRef = useRef(loadMoreReviews);
  loadMoreFnRef.current = loadMoreReviews;
  useEffect(() => {
    const rail = testimonialRailRef.current;
    const sentinel = railEndRef.current;
    if (!rail || !sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMoreFnRef.current();
      },
      { root: rail, rootMargin: "0px 540px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const totalRatings = reviewsData?.summary?.total_ratings ?? 0;
  // Điểm sao luôn cố định 4.9/5; chỉ số lượt đánh giá lấy thật (tăng dần theo BE).
  const summaryAvg = HOME_TESTIMONIAL_DISPLAY_AVG;
  const summaryCount = totalRatings > 0 ? totalRatings : FALLBACK_TESTIMONIAL_COUNT;

  const scrollTestimonials = (direction: -1 | 1) => {
    const rail = testimonialRailRef.current;
    if (!rail) return;
    rail.scrollLeft += direction * rail.clientWidth;
  };

  return (
    <section aria-labelledby="home-testimonials-title" className="space-y-3 md:space-y-5">
      <div className="px-1 md:flex md:items-center md:justify-between md:gap-6">
        <h2
          id="home-testimonials-title"
          className="font-serif-soft text-2xl text-foreground sm:text-3xl"
        >
          Người dùng nói gì về Bói Đê
        </h2>
        <div className="mt-2 flex items-center justify-between gap-3 md:mt-0 md:border-l md:border-gold-soft/50 md:pl-5">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Star className="h-4 w-4 fill-primary text-primary" aria-hidden="true" />
            <strong className="font-semibold text-foreground">{summaryAvg}/5</strong>
            <span>từ {summaryCount.toLocaleString("vi-VN")} lượt đánh giá</span>
          </div>
          <span className="shrink-0 text-xs text-primary md:hidden">Vuốt để xem</span>
          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => scrollTestimonials(-1)}
              aria-label="Xem các đánh giá trước"
              aria-controls="home-testimonials-rail"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gold-soft bg-background/45 text-muted-foreground transition hover:border-primary/60 hover:text-primary active:scale-95"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollTestimonials(1)}
              aria-label="Xem các đánh giá tiếp theo"
              aria-controls="home-testimonials-rail"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gold-soft bg-background/45 text-muted-foreground transition hover:border-primary/60 hover:text-primary active:scale-95"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={testimonialRailRef}
        id="home-testimonials-rail"
        onScroll={handleRailScroll}
        className="scrollbar-none flex snap-x snap-mandatory scroll-smooth gap-3 overflow-x-auto pb-2 motion-reduce:scroll-auto md:gap-4 lg:gap-6"
      >
        {testimonials.map((testimonial) => (
          <article
            key={testimonial.id}
            className="flex min-h-[252px] w-[84vw] max-w-[360px] shrink-0 snap-start flex-col rounded-3xl border border-gold-soft bg-card/70 p-5 shadow-mystic backdrop-blur transition duration-300 hover:border-primary/45 hover:shadow-gold sm:w-[72vw] md:w-[calc((100%-1rem)/2)] md:max-w-none md:p-6 lg:w-[calc((100%-3rem)/3)]"
          >
            <div
              className="flex items-center gap-1 text-primary"
              aria-label={`${testimonial.rating} trên 5 sao`}
            >
              {Array.from({ length: testimonial.rating }, (_, index) => (
                <Star key={index} className="h-4 w-4 fill-current" aria-hidden="true" />
              ))}
            </div>

            <p className="mt-4 line-clamp-4 flex-1 text-sm leading-6 text-foreground/82 sm:text-[15px]">
              “{testimonial.content}”
            </p>

            <div className="mt-5 flex items-center border-t border-gold-soft/35 pt-4">
              <span className="inline-flex items-center rounded-full border border-gold-soft bg-background/40 px-3 py-1 text-xs font-medium text-primary">
                {testimonial.experience}
              </span>
            </div>
          </article>
        ))}
        {loadingMore ? (
          <div
            className="flex min-h-[252px] w-20 shrink-0 items-center justify-center"
            aria-label="Đang tải thêm đánh giá"
          >
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-gold-soft border-t-primary" />
          </div>
        ) : null}
        <div ref={railEndRef} aria-hidden="true" className="w-px shrink-0" />
      </div>
    </section>
  );
}

type DashboardStatCard = {
  key: DailyFortuneCard["key"];
  icon: typeof Sun;
  label: string;
  value: string;
  hint: string;
};

const FALLBACK_STAT_CARDS: DashboardStatCard[] = [
  {
    key: "energy",
    icon: Sun,
    label: "Vận khí hôm nay",
    value: "Thượng cát",
    hint: "Tam hợp Thân-Tý-Thìn",
  },
  {
    key: "direction",
    icon: Compass,
    label: "Hướng tốt",
    value: "Đông Nam",
    hint: "Sinh khí • Diên niên",
  },
  {
    key: "element",
    icon: Flame,
    label: "Ngũ hành",
    value: "Hỏa vượng",
    hint: "Tránh Thủy, dụng Mộc",
  },
];

const STAT_ICON_BY_KEY: Record<DailyFortuneCard["key"], typeof Sun> = {
  energy: Sun,
  direction: Compass,
  element: Flame,
};

function getDashboardStatCards(cards?: DailyFortuneCard[]): DashboardStatCard[] {
  if (!cards?.length) return FALLBACK_STAT_CARDS;
  return cards.map((card) => ({
    ...card,
    icon: STAT_ICON_BY_KEY[card.key],
  }));
}

const INFO_LINKS = [
  { label: "Điều khoản dịch vụ", to: "/terms" },
  { label: "Chính sách bảo mật", to: "/privacy" },
  { label: "Bản quyền", to: "/copyright" },
  { label: "Hướng dẫn sử dụng", to: "/user-manual" },
  { label: "Câu hỏi thường gặp", to: "/faq" },
] as const;

// Kênh cộng đồng — dùng chung cho banner Cộng đồng + footer
const FACEBOOK_GROUP_URL = "https://www.facebook.com/groups/232657737355718";
const SOCIAL_LINKS = [
  { label: "TikTok Bói Đê", href: "https://www.tiktok.com/@boide.online", icon: <TikTokIcon /> },
  {
    label: "YouTube Bói Đê",
    href: "https://www.youtube.com/@boideonline",
    icon: <Youtube className="h-4 w-4" />,
  },
  {
    label: "Threads Bói Đê",
    href: "https://www.threads.com/@boide.online",
    icon: <ThreadsIcon />,
  },
  {
    label: "Instagram Bói Đê",
    href: "https://www.instagram.com/boide.online",
    icon: <Instagram className="h-4 w-4" />,
  },
  {
    label: "Fanpage Facebook Bói Đê",
    href: "https://www.facebook.com/profile.php?id=61591569002475",
    icon: <Facebook className="h-4 w-4" />,
  },
] as const;

function SiteFooter() {
  return (
    <footer className="mt-2 border-t border-gold-soft/20 pt-8">
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        {/* Thương hiệu + social */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <img
              src={logoIcon96}
              alt="Bói Đê"
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 object-contain drop-shadow-[0_0_8px_rgba(229,184,105,0.35)]"
            />
            <div>
              <p className="font-serif-soft text-xl leading-none text-gold-gradient">Bói Đê</p>
              <p className="mt-1 text-[11px] italic text-primary/70">
                Gieo quẻ soi lối, Bói Đê dẫn đường
              </p>
            </div>
          </div>
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
            Bói Đê đồng hành cùng bạn khám phá vận mệnh — gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát
            Tự, luận giải bằng AI chuẩn xác.
          </p>
          <div className="flex flex-wrap gap-2">
            {SOCIAL_LINKS.map((s) => (
              <SocialIcon key={s.label} href={s.href} label={s.label}>
                {s.icon}
              </SocialIcon>
            ))}
            <SocialIcon href={FACEBOOK_GROUP_URL} label="Nhóm Facebook Bói Đê">
              <Users className="h-4 w-4" />
            </SocialIcon>
          </div>
        </div>

        {/* Thông tin đơn vị vận hành */}
        <BusinessInfoBlock />
      </div>

      {/* Pháp lý + copyright */}
      <div className="mt-8 flex flex-col items-center gap-3 border-t border-gold-soft/10 pt-5 sm:flex-row sm:justify-between">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
          {INFO_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="transition hover:text-primary">
              {link.label}
            </Link>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">© 2026 Bói Đê</p>
      </div>
    </footer>
  );
}

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
      aria-label={label}
      title={label}
      className="group flex h-10 w-10 items-center justify-center rounded-xl border border-gold-soft/20 bg-background/25 text-muted-foreground transition-all duration-300 hover:border-primary/50 hover:bg-background/40 hover:text-primary md:hover:-translate-y-0.5"
    >
      {children}
    </a>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12.8 2h3.1c.2 1.6 1.1 3 2.4 3.9.7.5 1.6.7 2.5.8v3.2c-1.7 0-3.3-.5-4.6-1.4v6.4c0 3.4-2.8 6.2-6.2 6.1-3.1-.1-5.6-2.6-5.7-5.7-.1-3.4 2.6-6.1 6-6.1.3 0 .6 0 .9.1v3.3c-.3-.1-.6-.2-1-.2-1.5 0-2.7 1.3-2.6 2.8.1 1.4 1.3 2.5 2.7 2.5 1.5 0 2.8-1.2 2.8-2.7V2z" />
    </svg>
  );
}

function ThreadsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12.2 22C6.7 22 3 18 3 12S6.7 2 12.2 2c3.9 0 6.8 1.7 8.3 4.9l-1.9.9c-1.1-2.3-3.1-3.6-6.4-3.6C8 4.2 5.1 7 5.1 12s2.9 7.8 7.1 7.8c2.9 0 4.8-1.2 5.6-3.1.5-1.1.4-2.2-.1-3-.5-.9-1.5-1.5-2.8-1.8 0 1.6-.4 2.8-1.2 3.6-.7.7-1.6 1-2.8 1-1.1 0-2-.4-2.6-1-.7-.7-.9-1.6-.7-2.5.3-1.7 1.9-2.7 4.1-2.7.6 0 1.1 0 1.6.1 0-1.4-.8-2.2-2.1-2.2-1 0-1.8.4-2.3 1.2l-1.6-1.1c.9-1.3 2.3-2 4-2 2.6 0 4.1 1.7 4.1 4.4v.5c1.6.5 2.8 1.5 3.4 2.9.7 1.5.6 3.5-.5 5.2C18.6 20.5 15.8 22 12.2 22zm.4-8.6c-1.3 0-2.2.5-2.3 1.3-.1.7.5 1.1 1.4 1.1.7 0 1.2-.2 1.6-.6.4-.5.6-1.2.6-2.1-.5-.1-.9-.1-1.3-.1z" />
    </svg>
  );
}

function formatMoney(amount: number): string {
  return amount.toLocaleString("vi-VN");
}

function HomeTopupCTA() {
  const hasToken = typeof window !== "undefined" ? !!getBoideToken() : false;

  const { data: progress, isLoading } = useQuery({
    queryKey: ["monthly-topup-progress"],
    queryFn: getMonthlyTopupProgress,
    enabled: hasToken,
    staleTime: 1000 * 60 * 5,
  });

  if (!hasToken || isLoading || !progress) return null;

  const currentPercent = Math.round(progress.unlocked_rate * 100);

  return (
    <Link
      to="/thanh-toan"
      className="group relative block overflow-hidden rounded-2xl border border-gold-soft/30 bg-background/35 hover:bg-background/50 px-4 py-3 transition-all duration-300 shadow-sm"
    >
      <div className="absolute inset-0 stardust opacity-[0.08]" />
      <div className="relative flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
            <Coins className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="text-xs md:text-sm font-bold text-foreground tracking-tight">
                Ưu đãi tích lũy mốc nạp
              </span>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold border transition-colors shadow-sm",
                  currentPercent > 0
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/15"
                    : "bg-primary/10 text-primary border-primary/15",
                )}
              >
                +{currentPercent}%
              </span>
            </div>
            <p className="text-[11px] md:text-xs text-muted-foreground/90 mt-0.5 font-medium truncate">
              {progress.next_threshold ? (
                <>
                  Đã nạp{" "}
                  <strong className="text-foreground font-semibold">
                    {formatMoney(progress.paid_amount)}đ
                  </strong>{" "}
                  / {formatMoney(progress.next_threshold)}đ (Còn thiếu{" "}
                  {formatMoney(progress.remaining_amount)}đ)
                </>
              ) : (
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                  Bạn đã đạt mốc ưu đãi tối đa!
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {progress.next_threshold && (
            <div className="w-14 sm:w-20 md:w-24">
              <Progress
                value={progress.progress_percent}
                className="h-1.5 bg-primary/15 border-none shadow-none rounded-full"
              />
            </div>
          )}
          <ChevronRight className="size-4 text-muted-foreground/60 group-hover:translate-x-0.5 group-hover:text-primary transition-all duration-300" />
        </div>
      </div>
    </Link>
  );
}
